import { StyleSheet, Text, View } from 'react-native'
import { PointsLedgerItemQueryResult } from '../gql/getPointsLedger.query'
import { memo, useMemo } from 'react'
import { PurchaseIcon } from '@/src/assets/icons/PurchaseIcon'
import { formatCurrency, formatNumber } from '@/src/common/utils/general'
import ReturnIcon from '@/src/assets/icons/ReturnIcon'
import { formatToTextMonth } from '@/src/common/utils/date'
import { HomeOutlined } from '@/src/assets/icons/HomeOutlined'
import { BankOutlined } from '@/src/assets/icons/BankOutlined'
import CashOutPointsIcon from '@/src/assets/icons/CashOutPoints'
import { PointsLedgerAmount } from '@api/mesa/graphql'
import { mesaColorNames, spacing, typography } from '@/src/common/theme'
import { Mortgage } from '@/src/assets/icons/Mortgage'

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    marginVertical: 8,
  },
  iconContainer: {
    backgroundColor: mesaColorNames['Wide/100 #C6FFF5'],
    borderRadius: spacing['md'],
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
    width: 36,
    height: 36,
  },
  column: {
    gap: 8,
  },
  left: {
    flexGrow: 1,
  },
  right: {
    alignItems: 'flex-end',
    flexShrink: 0,
  },
  title: {
    ...typography['label/sm-regular'],
    paddingRight: 12,
  },
  subtitle: {
    ...typography['label/xs-regular'],
    color: mesaColorNames['semantic/fg/subtle #898D8F'],
  },
  points_default: {
    ...typography['label/sm-bold'],
  },
  points_muted: {
    ...typography['label/sm-bold'],
    color: mesaColorNames['semantic/fg/subtle #898D8F'],
  },
  points_success: {
    ...typography['label/sm-bold'],
    color: mesaColorNames['semantic/fg/success #008557'],
  },
  points_warning: {
    ...typography['label/sm-bold'],
    color: mesaColorNames['Mesa Third #E78A43'],
  },
})

const getSignedPointsAmount = (points: PointsLedgerAmount) => {
  if (points.isNegative) {
    return points.value * -1
  }
  return points.value
}

export interface ActivityFeedItemProps {
  icon: React.ReactNode
  title: string
  subtitle?: string
  secondarySubtitle?: string
  points: number
  variant?: 'default' | 'muted' | 'success' | 'warning'
}

export const ActivityFeedItem = ({
  icon,
  title,
  subtitle,
  secondarySubtitle,
  points,
  variant = 'default',
}: ActivityFeedItemProps) => (
  <View style={styles.container}>
    <View style={styles.iconContainer}>{icon}</View>
    <View style={[styles.left, styles.column, { flex: 1 }]}>
      <Text style={styles.title} numberOfLines={2} ellipsizeMode="tail">
        {title}
      </Text>
      {subtitle && <Text style={styles.subtitle}>{subtitle}</Text>}
    </View>
    <View style={[styles.right, styles.column]}>
      <Text style={styles[`points_${variant}`]}>{formatNumber(points)} Points</Text>
      {secondarySubtitle && <Text style={styles.subtitle}>{secondarySubtitle}</Text>}
    </View>
  </View>
)

type ActivityFeedItemContainerProps = {
  item: PointsLedgerItemQueryResult
}

const ActivityFeedItemContainer = ({ item }: ActivityFeedItemContainerProps) => {
  const props = useMemo(() => getActivityFeedDisplay(item), [item])
  return <ActivityFeedItem {...props} />
}

export const RewardsComponentsActivityFeedItem = memo(ActivityFeedItemContainer)

const getActivityFeedDisplay = (item: PointsLedgerItemQueryResult): ActivityFeedItemProps => {
  const { __typename, points } = item

  switch (__typename) {
    case 'CardPurchasePointsLedgerItem':
      return {
        icon: <PurchaseIcon />,
        title: item.merchantName,
        subtitle: formatToTextMonth(new Date(item.date)),
        secondarySubtitle: formatCurrency(item.transactionAmount.value.toString(), {
          decimals: item.transactionAmount.decimalPlaces,
          isFloat: false,
          minimumFractionDigits: 2,
        }),
        variant: item.isPending ? 'muted' : 'default',
        points: getSignedPointsAmount(points),
      } satisfies ActivityFeedItemProps
    case 'CardReturnPointsLedgerItem':
      return {
        icon: <ReturnIcon />,
        title: item.merchantName,
        subtitle: formatToTextMonth(new Date(item.date)),
        secondarySubtitle: formatCurrency(item.transactionAmount.value.toString(), {
          decimals: item.transactionAmount.decimalPlaces,
          isFloat: false,
          minimumFractionDigits: 2,
        }),
        variant: item.isPending ? 'muted' : 'warning',
        points: getSignedPointsAmount(points),
      } satisfies ActivityFeedItemProps
    case 'MortgageSpendPointsLedgerItem':
    case 'MortgageLoanPointsLedgerItem':
      return {
        icon: <HomeOutlined outlineColor={mesaColorNames['Mesa - Brand/Secondary #223843']} />,
        title: 'MORTGAGE REWARDS',
        subtitle: formatToTextMonth(new Date(item.date)),
        points: getSignedPointsAmount(points),
        variant: item.isPending ? 'muted' : 'default',
      } satisfies ActivityFeedItemProps
    case 'GoodwillPointsLedgerItem':
      return {
        icon: (
          <BankOutlined pathProps={{ stroke: mesaColorNames['Mesa - Brand/Secondary #223843'] }} />
        ),
        title: item.notes ?? 'ADJUSTMENT',
        subtitle: formatToTextMonth(new Date(item.date)),
        points: getSignedPointsAmount(points),
      } satisfies ActivityFeedItemProps
    case 'UnknownEarnPointsLedgerItem':
      return {
        icon: (
          <BankOutlined pathProps={{ stroke: mesaColorNames['Mesa - Brand/Secondary #223843'] }} />
        ),
        title: 'ADJUSTMENT',
        subtitle: formatToTextMonth(new Date(item.date)),
        points: getSignedPointsAmount(points),
      } satisfies ActivityFeedItemProps
    case 'ExternalRedemptionPointsLedgerItem':
      return {
        icon: <CashOutPointsIcon width={24} height={24} />,
        title: item.orderDescription,
        subtitle: formatToTextMonth(new Date(item.date)),
        points: getSignedPointsAmount(points),
        variant: 'success',
      } satisfies ActivityFeedItemProps
    case 'ExternalRedemptionRefundPointsLedgerItem':
      return {
        icon: <CashOutPointsIcon width={24} height={24} />,
        title: item.orderDescription,
        subtitle: formatToTextMonth(new Date(item.date)),
        points: getSignedPointsAmount(points),
      } satisfies ActivityFeedItemProps
    case 'MortgageTransferPointsLedgerItem': {
      const isSuccessfulState = ['pending', 'settled'].includes(item.status)
      const isPendingState = item.status === 'pending'
      const variant = isPendingState ? 'muted' : isSuccessfulState ? 'success' : 'warning'
      return {
        icon: <Mortgage />,
        title: 'Mesa Points for Mortgage',
        subtitle: `${formatToTextMonth(new Date(item.date))}${
          isSuccessfulState ? '' : ' - FAILED'
        }`,
        points: isSuccessfulState ? getSignedPointsAmount(points) : 0,
        variant,
      } satisfies ActivityFeedItemProps
    }
    case 'StatementCreditRedemptionPointsLedgerItem':
      return {
        icon: <CashOutPointsIcon width={24} height={24} />,
        title: 'STATEMENT CREDIT',
        subtitle: formatToTextMonth(new Date(item.date)),
        points: getSignedPointsAmount(points),
        secondarySubtitle: formatCurrency(item.amount.value.toString(), {
          decimals: item.amount.decimalPlaces,
          isFloat: false,
          minimumFractionDigits: 2,
        }),
        variant: 'success',
      } satisfies ActivityFeedItemProps
    case 'UnknownSpendPointsLedgerItem':
      return {
        icon: <CashOutPointsIcon width={24} height={24} />,
        title: 'REDEMPTION',
        subtitle: formatToTextMonth(new Date(item.date)),
        points: getSignedPointsAmount(points),
        variant: 'success',
      } satisfies ActivityFeedItemProps
    case 'ReferralBonusPointsLedgerItem':
      return {
        icon: (
          <BankOutlined pathProps={{ stroke: mesaColorNames['Mesa - Brand/Secondary #223843'] }} />
        ),
        title: 'REFERRAL BONUS',
        subtitle: formatToTextMonth(new Date(item.date)),
        points: getSignedPointsAmount(points),
      } satisfies ActivityFeedItemProps
    case 'PromoCodeBonusPointsLedgerItem': {
      let title: string
      if (item.promoCode === 'summer_2025_bonus') {
        title = 'SUMMER SPEND BONUS'
      } else if (item.promoCode === 'referral_bonus_offer') {
        title = 'REFERRAL SPEND BONUS OFFER'
      } else {
        title = 'SIGN UP BONUS'
      }
      return {
        icon: <CashOutPointsIcon width={24} height={24} />,
        title,
        subtitle: formatToTextMonth(new Date(item.date)),
        points: getSignedPointsAmount(points),
        variant: item.isPending ? 'muted' : 'default',
      } satisfies ActivityFeedItemProps
    }
    case 'MesaMortgageLoanPointsLedgerItem':
      return {
        icon: <HomeOutlined outlineColor={mesaColorNames['Mesa - Brand/Secondary #223843']} />,
        title: 'Mesa Mortgage Rewards',
        subtitle: formatToTextMonth(new Date(item.date)),
        points: getSignedPointsAmount(points),
      } satisfies ActivityFeedItemProps
    case 'LoyaltyTierEventPointsLedgerItem':
      return {
        icon: <HomeOutlined outlineColor={mesaColorNames['Mesa - Brand/Secondary #223843']} />,
        title: item.reason,
        subtitle: formatToTextMonth(new Date(item.eventDate)),
        points: getSignedPointsAmount(points),
      } satisfies ActivityFeedItemProps
    case 'SunsetFlushPointsLedgerItem':
      return {
        icon: (
          <BankOutlined pathProps={{ stroke: mesaColorNames['Mesa - Brand/Secondary #223843'] }} />
        ),
        title: 'Points Finalization',
        subtitle: formatToTextMonth(new Date(item.date)),
        points: getSignedPointsAmount(points),
      } satisfies ActivityFeedItemProps
    default: {
      item satisfies never // Nice little TS hint if the API changes and we forget to update this switch

      // Cast to the base type to make the app forward-compatible with API changes
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      const fallbackForUnknownItem = item as PointsLedgerItemQueryResult

      if (fallbackForUnknownItem.points.isNegative) {
        return {
          icon: (
            <BankOutlined
              pathProps={{ stroke: mesaColorNames['Mesa - Brand/Secondary #223843'] }}
            />
          ),
          title: 'MISCELLANEOUS',
          subtitle: formatToTextMonth(new Date(fallbackForUnknownItem.date)),
          points: getSignedPointsAmount(points),
          variant: 'warning',
        } satisfies ActivityFeedItemProps
      }

      return {
        icon: (
          <BankOutlined pathProps={{ stroke: mesaColorNames['Mesa - Brand/Secondary #223843'] }} />
        ),
        title: 'MISCELLANEOUS',
        subtitle: formatToTextMonth(new Date(fallbackForUnknownItem.date)),
        points: getSignedPointsAmount(points),
      } satisfies ActivityFeedItemProps
    }
  }
}
